import { useSampleCategories } from "app/hooks/shared/sample/useSampleCategories";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  CategoryStandardProduct,
  StandardProductCategory,
} from "shared/api/middleware-api-client";
import { REACT_APP_GERS_API_URL } from "shared/config";
import Icon, { IconTypes } from "shared/ui/icon";
import OptionTemplate from "shared/ui/option-template";
import Text from "shared/ui/typography/Text";
import { concatClassnames } from "shared/utils/concatClassnames";
import SelectedSamples from "../selected-samples";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faMinus, faPlus } from "@fortawesome/free-solid-svg-icons";
import { useSearchParams } from "react-router-dom";
import { resolveInitialSampleSelection } from "./selectionResolver";

interface Props {
  selectedSamples: CategoryStandardProduct[];
  setSelectedSamples: (sample: CategoryStandardProduct[]) => void;

  selectedSample: CategoryStandardProduct | undefined;
  setSelectedSample: (sample: CategoryStandardProduct) => void;
}

const getCategoryDisplayName = (category: StandardProductCategory): string => {
  if (category.code === "kleurstaalaccessoireakoestischpaneel") {
    return "Kleurstaal eindprofielen";
  }

  return category.name;
};

const SamplesOptions = ({
  setSelectedSample,
  selectedSample,
  selectedSamples,
  setSelectedSamples,
}: Props) => {
  const { sampleCategories } = useSampleCategories();

  const [selectedCategory, setSelectedCategory] = useState<
    StandardProductCategory | undefined
  >();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showScrollFade, setShowScrollFade] = useState(false);
  const listRef = useRef<HTMLUListElement>(null);

  const checkScrollable = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    const hasOverflow = el.scrollHeight > el.clientHeight;
    const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 16;
    setShowScrollFade(hasOverflow && !isAtBottom);
  }, []);

  const [searchParams] = useSearchParams();
  const selectedProductCategory = searchParams.get("selectedCategory");
  const selectedProductId = searchParams.get("standard-product");

  useEffect(() => {
    if (!sampleCategories.length) return;

    const {
      selectedCategory: resolvedCategory,
      selectedSample: resolvedSample,
    } = resolveInitialSampleSelection({
      sampleCategories,
      standardProductId: selectedProductId,
      selectedCategoryParam: selectedProductCategory,
    });

    if (resolvedCategory) {
      setSelectedCategory(resolvedCategory);
    }

    if (resolvedSample) {
      setSelectedSample(resolvedSample);
    }
  }, [
    sampleCategories,
    selectedProductCategory,
    selectedProductId,
    setSelectedSample,
  ]);

  const onSampleSelectClick = (
    sample: CategoryStandardProduct,
    isSelected: boolean,
  ) => {
    if (isSelected) {
      setSelectedSamples(selectedSamples.filter((s) => s.id !== sample.id));
    } else {
      setSelectedSamples([...selectedSamples, sample]);
      setSelectedSample(sample);
    }
  };

  const handleDropdownKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setIsDropdownOpen(false);
    }
  };

  return (
    <div className="gg-grid gg-gap-y-8">
      <OptionTemplate
        title="Kies een collectie"
        description="Kies hieronder eerst een collectie waarvan je een kleurstaal wilt bestellen."
        enablePadding={false}
        className="gg-border-b-0"
        showInfo={false}
      >
        <div className="gg-relative" onKeyDown={handleDropdownKeyDown}>
          <button
            type="button"
            aria-expanded={isDropdownOpen}
            aria-controls="collection-dropdown"
            className="gg-flex gg-w-full gg-cursor-pointer gg-items-center gg-justify-between gg-rounded-sm gg-border gg-border-dark-blue-200 gg-bg-white gg-px-3.5 gg-py-2.5 gg-text-left gg-text-sm gg-font-bold"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          >
            <Text className="gg-flex gg-items-center">
              {selectedCategory !== undefined
                ? getCategoryDisplayName(selectedCategory)
                : "Maak een selectie"}
            </Text>

            <Icon
              name={IconTypes.ARROW_LEFT}
              className={concatClassnames(
                isDropdownOpen ? "gg-rotate-90" : "-gg-rotate-90",
                "gg-opacity-50 gg-transition-all",
              )}
            />
          </button>

          {isDropdownOpen && (
            <div
              id="collection-dropdown"
              role="listbox"
              className="gg-absolute gg-left-0 gg-right-0 gg-top-full gg-z-30 gg-max-h-64 gg-overflow-y-auto gg-rounded-b-sm gg-border gg-border-t-0 gg-border-dark-blue-200 gg-bg-white gg-shadow-lg"
            >
              {sampleCategories
                .filter((category) => category.id !== selectedCategory?.id)
                .map((category) => (
                  <button
                    type="button"
                    role="option"
                    aria-selected={false}
                    className="gg-block gg-w-full gg-cursor-pointer gg-border-b gg-border-dark-blue-200 gg-px-3 gg-py-1.5 gg-text-left last:gg-border-b-0 hover:gg-bg-gray-100"
                    key={`sample-category-${category.id}-key`}
                    onClick={() => {
                      setSelectedCategory(category);
                      const categoryFirstProduct = category.product[0];

                      if (categoryFirstProduct) {
                        setSelectedSample(categoryFirstProduct.standardProduct);
                      }

                      setIsDropdownOpen(false);
                    }}
                  >
                    <Text className="gg-text-sm" bold>
                      {getCategoryDisplayName(category)}
                    </Text>
                  </button>
                ))}
            </div>
          )}
        </div>
      </OptionTemplate>

      <OptionTemplate
        title="Kies een kleur"
        className="gg-border-b-0 !gg-pt-0 gg-pb-0 gg-pl-0 gg-pr-0"
        enablePadding={false}
        showInfo={false}
      >
        <div className="gg-relative">
          <AnimatePresence mode="wait">
            <motion.ul
              ref={listRef}
              key={selectedCategory?.id ?? "empty"}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
              onAnimationComplete={checkScrollable}
              onScroll={checkScrollable}
              className={concatClassnames(
                "gg-relative gg-mt-1 gg-flex gg-h-[28rem] gg-flex-wrap gg-content-start gg-gap-4 gg-overflow-y-auto sm:gg-gap-5",
              )}
            >
              {selectedCategory !== undefined
                ? selectedCategory.product.map((product, i) => {
                    const isSelected = selectedSamples.some(
                      (s) => s.id === product.standardProduct.id,
                    );

                    return (
                      <motion.li
                        className="gg-pt-3 gg-text-left"
                        key={product.standardProduct.id}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          duration: 0.2,
                          delay: i * 0.03,
                          ease: [0.25, 0.1, 0.25, 1],
                        }}
                      >
                        <div className="gg-group gg-relative gg-cursor-pointer gg-overflow-visible">
                          <div className="gg-absolute gg-bottom-0 gg-left-0 gg-right-0 gg-top-0 gg-z-10 gg-hidden gg-h-full gg-w-full gg-bg-dark-blue-100 group-focus-within:gg-block group-hover:gg-block"></div>

                          <div className="gg-absolute gg-left-1/2 gg-top-1/2 gg-z-20 gg-hidden -gg-translate-x-1/2 -gg-translate-y-1/2 gg-gap-x-2 group-focus-within:gg-flex group-hover:gg-flex">
                            <button
                              aria-label={
                                isSelected
                                  ? "Deselecteer kleurstaal"
                                  : "Selecteer kleurstaal"
                              }
                              className="gg-flex gg-h-8 gg-w-8 gg-items-center gg-justify-center gg-rounded-full gg-bg-yellow"
                              onClick={(e) => {
                                e.currentTarget.blur();
                                onSampleSelectClick(
                                  product.standardProduct,
                                  isSelected,
                                );
                              }}
                            >
                              {isSelected ? (
                                <FontAwesomeIcon icon={faMinus} />
                              ) : (
                                <FontAwesomeIcon icon={faPlus} />
                              )}
                            </button>

                            <button
                              aria-label="Bekijk kleurstaal"
                              onClick={(e) => {
                                e.currentTarget.blur();
                                setSelectedSample(product.standardProduct);
                              }}
                              className="gg-flex gg-h-8 gg-w-8 gg-items-center gg-justify-center gg-rounded-full gg-bg-dark-blue gg-text-white"
                            >
                              <FontAwesomeIcon icon={faEye} />
                            </button>
                          </div>

                          {product.standardProduct.thumbnailId.length !== 0 ? (
                            <img
                              className="gg-mb-2 gg-h-20 gg-w-20 gg-rounded gg-border gg-border-dark-blue gg-bg-contain gg-shadow-main gg-transition-all sm:gg-h-24 sm:gg-w-24"
                              src={`${REACT_APP_GERS_API_URL}/StandardProducts/download-file/${product.standardProduct.thumbnailId}`}
                              alt={product.standardProduct.mainImageAltText}
                            />
                          ) : (
                            <div className="gg-mb-2 gg-h-20 gg-w-20 gg-rounded gg-border gg-border-dark-blue sm:gg-h-24 sm:gg-w-24"></div>
                          )}

                          {isSelected && (
                            <span
                              className={concatClassnames(
                                "gg-flex gg-h-6 gg-w-6 gg-bg-emerald",
                                "gg-items-center gg-justify-center gg-rounded-full",
                                "gg-absolute -gg-right-3 -gg-top-3",
                              )}
                            >
                              <Icon
                                name={IconTypes.WHITE_CHECK}
                                className="gg-h-2"
                              />
                            </span>
                          )}
                        </div>

                        <Text className="gg-w-20 gg-text-[10px] gg-uppercase sm:gg-text-xs">
                          {product.standardProduct.shortname}
                        </Text>
                      </motion.li>
                    );
                  })
                : Array(8)
                    .fill(0)
                    .map((_, i) => (
                      <li
                        className="gg-text-left"
                        key={`category-product-placeholder-${i}`}
                      >
                        <div className="gg-mb-2 gg-h-20 gg-w-20 gg-rounded gg-border gg-border-dashed gg-border-dark-blue-700 sm:gg-h-24 sm:gg-w-24"></div>
                        <div className="gg-h-4 gg-w-12 gg-rounded-full gg-bg-grey-600 sm:gg-h-5 sm:gg-w-16"></div>
                      </li>
                    ))}
              {selectedCategory === undefined && (
                <Text className="gg-absolute gg-left-1/2 gg-top-1/2 gg-z-10 gg-w-full gg-max-w-md -gg-translate-x-1/2 -gg-translate-y-1/2 gg-bg-white gg-px-9 gg-py-8 gg-text-center gg-text-sm gg-shadow-main">
                  Maak eerst een keuze uit een van onze collecties voor{" "}
                  <br className="gg-hidden sm:gg-flex" /> je de beschikbare
                  samples ziet.
                </Text>
              )}
            </motion.ul>
          </AnimatePresence>

          {showScrollFade && (
            <div
              className="gg-pointer-events-none gg-absolute gg-bottom-0 gg-left-0 gg-right-0 gg-h-16 gg-bg-gradient-to-t gg-from-white gg-to-transparent gg-transition-opacity gg-duration-300"
              aria-hidden="true"
            />
          )}
        </div>
      </OptionTemplate>

      <SelectedSamples
        selectedSamples={selectedSamples}
        setSelectedSamples={setSelectedSamples}
        className="gg-flex sm:gg-hidden"
      />
    </div>
  );
};
export default SamplesOptions;
